import { OpusDecoder } from "opus-decoder"
import { interleave } from "../../ring-buffer/audioqueue"
import { OggOpusIndex } from "./OggOpusIndex"

const OUTPUT_CHANNELS = 2
const OPUS_SAMPLE_RATE = 48000 as const
const SEEK_PREROLL_SAMPLES = 3840

/**
 * Stateful decoder for a single persisted Opus region.
 *
 * `OggOpusIndex` handles the one-time container parsing; this class handles the
 * incremental runtime work needed by playback:
 *
 * - seek to a target song-local timestamp
 * - decode forward from an indexed Ogg page with RFC-mandated preroll
 * - trim preroll and final-page padding
 * - expose a steady stream of stereo PCM frames to the track scheduler
 *
 * The worker creates one decoder per active or prewarmed region rather than one
 * global decoder for the whole song.
 */
export class OpusRegionDecoder {
  private readonly decoder: OpusDecoder<typeof OPUS_SAMPLE_RATE>
  private readonly index: OggOpusIndex
  private encodeFromGranulePosition: bigint | null = null
  private ended = false
  private nextPageIndex = 0
  private pendingPcm: Float32Array<ArrayBufferLike> = new Float32Array(0)
  private pendingPcmEnd = 0
  private pendingPcmStart = 0
  private playbackPhase = 0
  private preRollSamplesToDrop = 0

  /**
   * Creates the underlying Opus decoder with metadata from the parsed stream.
   */
  constructor(index: OggOpusIndex) {
    this.index = index
    this.decoder = new OpusDecoder<typeof OPUS_SAMPLE_RATE>({
      channelMappingTable: index.header.channelMappingTable,
      channels: index.header.channels,
      coupledStreamCount: index.header.coupledStreamCount,
      forceStereo: index.header.channels === 1,
      preSkip: index.header.preSkip,
      sampleRate: OPUS_SAMPLE_RATE,
      streamCount: index.header.streamCount,
    })
  }

  get ready(): Promise<void> {
    return this.decoder.ready
  }

  /**
   * Exposes region duration derived from the parsed Ogg granule positions.
   */
  get durationSeconds(): number {
    return this.index.durationSeconds
  }

  /**
   * Signals that the decoder has reached end-of-stream and has no PCM buffered.
   */
  get isExhausted(): boolean {
    return this.ended && this.getPendingSampleCount() === 0
  }

  /**
   * Frees the underlying wasm decoder state.
   */
  async destroy(): Promise<void> {
    await this.ready
    this.decoder.free()
  }

  /**
   * Repositions the decoder to a region-local timestamp.
   *
   * Seeking is coarse at the Ogg-page level: we convert the target time to a
   * pre-skip-inclusive granule position, back up by the RFC 7845 §4.6 preroll,
   * and choose the earliest page that can safely anchor decoding from there.
   */
  async seek(timestamp: number): Promise<void> {
    await this.ready
    await this.decoder.reset()

    this.clearPendingPcm()
    this.playbackPhase = 0
    this.ended = false

    const targetGranulePosition = this.secondsToGranulePosition(timestamp)

    // RFC 7845 Section 4.6 requires at least 80 ms of pre-roll when seeking so
    // the decoder has enough packet history to produce the correct target audio.
    const prerollStartGranule = targetGranulePosition - BigInt(SEEK_PREROLL_SAMPLES)
    this.encodeFromGranulePosition = prerollStartGranule > 0n ? prerollStartGranule : 0n
    this.preRollSamplesToDrop = Number(targetGranulePosition - this.encodeFromGranulePosition)
    this.nextPageIndex = this.index.findPageIndexAtOrAfterGranule(this.encodeFromGranulePosition)

    if (this.nextPageIndex >= this.index.pages.length) {
      this.ended = true
      this.encodeFromGranulePosition = null
      this.preRollSamplesToDrop = 0
    }
  }

  /**
   * Produces interleaved stereo PCM for the mixer.
   *
   * The decoder keeps a queue of already-decoded PCM. If that queue is too small
   * for the requested render window, it decodes more Ogg pages first. Playback
   * rate changes are handled here by resampling across the queued PCM with simple
   * linear interpolation.
   */
  readFrames(outputFrameCount: number, playbackRate: number): Float32Array {
    if (outputFrameCount <= 0) {
      return new Float32Array(0)
    }

    const output = new Float32Array(outputFrameCount * OUTPUT_CHANNELS)
    this.readFramesInto(output, outputFrameCount, playbackRate)
    return output
  }

  /**
   * Fills a caller-provided interleaved stereo buffer for the mixer hot path.
   */
  readFramesInto(
    output: Float32Array<ArrayBufferLike>,
    outputFrameCount: number,
    playbackRate: number
  ): void {
    const outputSampleCount = outputFrameCount * OUTPUT_CHANNELS
    if (output.length < outputSampleCount) {
      throw new RangeError(
        `output too small: need ${outputSampleCount} samples, got ${output.length}`
      )
    }

    output.fill(0, 0, outputSampleCount)

    const safePlaybackRate = playbackRate > 0 ? playbackRate : 1
    const requiredInputFrames = Math.ceil(
      this.playbackPhase + Math.max(0, outputFrameCount - 1) * safePlaybackRate
    ) + 1
    this.ensureDecodedFrames(requiredInputFrames)

    const availableFrames = this.getPendingFrameCount()

    if (availableFrames === 0) {
      return
    }

    let sourcePosition = this.playbackPhase
    let producedFrames = 0

    while (producedFrames < outputFrameCount) {
      const baseFrame = Math.floor(sourcePosition)
      if (baseFrame >= availableFrames) {
        break
      }

      const fraction = sourcePosition - baseFrame
      const nextFrame = Math.min(baseFrame + 1, availableFrames - 1)
      const outputOffset = producedFrames * OUTPUT_CHANNELS
      const baseOffset = this.pendingPcmStart + baseFrame * OUTPUT_CHANNELS
      const nextOffset = this.pendingPcmStart + nextFrame * OUTPUT_CHANNELS

      for (let channel = 0; channel < OUTPUT_CHANNELS; channel++) {
        const start = this.pendingPcm[baseOffset + channel] ?? 0
        const end = this.pendingPcm[nextOffset + channel] ?? 0
        output[outputOffset + channel] = start + (end - start) * fraction
      }

      sourcePosition += safePlaybackRate
      producedFrames++
    }

    const framesToConsume = Math.min(Math.floor(sourcePosition), availableFrames)
    this.pendingPcmStart += framesToConsume * OUTPUT_CHANNELS

    if (this.pendingPcmStart >= this.pendingPcmEnd) {
      this.clearPendingPcm()
      this.playbackPhase = 0
      return
    }

    this.playbackPhase = sourcePosition - framesToConsume
  }

  /**
   * Decodes the next Ogg page worth of Opus packets into pending PCM.
   *
   * This is where the seek anchor is refined into an exact audible start point:
   * the first page can skip whole Opus frames before decode, then any remaining
   * sub-frame preroll is dropped from the decoded PCM afterwards.
   */
  private decodeNextPage(): void {
    const page = this.index.getPage(this.nextPageIndex)
    if (!page) {
      this.ended = true
      return
    }

    this.nextPageIndex++

    if (page.codecFrames.length === 0) {
      return
    }

    let firstFrameIndex = 0
    let skippedSamples = 0
    const pageInfo = this.index.getSeekablePageInfo(this.nextPageIndex - 1)

    if (this.encodeFromGranulePosition !== null) {
      if (pageInfo !== null) {
        const samplesToSkip = Math.max(
          0,
          Number(this.encodeFromGranulePosition - pageInfo.startGranulePosition)
        )

        while (
          firstFrameIndex < page.codecFrames.length &&
          skippedSamples + page.codecFrames[firstFrameIndex].samples <= samplesToSkip
        ) {
          skippedSamples += page.codecFrames[firstFrameIndex].samples
          firstFrameIndex++
        }

        this.preRollSamplesToDrop += samplesToSkip - skippedSamples
      }

      this.encodeFromGranulePosition = null
    }

    if (firstFrameIndex >= page.codecFrames.length) {
      return
    }

    const decoded = this.decoder.decodeFrames(
      page.codecFrames.slice(firstFrameIndex).map((frame) => frame.data)
    )
    if (decoded.errors.length > 0) {
      throw new Error(decoded.errors.map((error) => error.message).join(", "))
    }

    let decodedFrameCount = decoded.samplesDecoded

    // RFC 7845 Section 4.4: the granule position on the final page can trim the
    // decoded tail, so only the indicated number of samples are part of the stream.
    if (page.isLastPage && pageInfo !== null) {
      const decodedStartGranulePosition =
        pageInfo.startGranulePosition + BigInt(skippedSamples)
      const keepFrameCount = Number(
        pageInfo.granulePosition - decodedStartGranulePosition
      )
      if (keepFrameCount >= 0) {
        decodedFrameCount = Math.min(decodedFrameCount, keepFrameCount)
      }
    }

    const stereoPcm = this.toStereoInterleaved(decoded.channelData, decodedFrameCount)
    const trimmedPcm = this.dropPrerollSamples(stereoPcm)
    this.appendPendingPcm(trimmedPcm)

    if (page.isLastPage) {
      this.ended = true
    }
  }

  /**
   * Drops decoded preroll samples after a seek until the requested target point
   * becomes the first audible sample.
   */
  private dropPrerollSamples(pcm: Float32Array): Float32Array {
    if (this.preRollSamplesToDrop === 0) {
      return pcm
    }

    const availableFrames = pcm.length / OUTPUT_CHANNELS
    if (this.preRollSamplesToDrop >= availableFrames) {
      this.preRollSamplesToDrop -= availableFrames
      return new Float32Array(0)
    }

    const trimmed = pcm.subarray(this.preRollSamplesToDrop * OUTPUT_CHANNELS)
    this.preRollSamplesToDrop = 0
    return trimmed
  }

  /**
   * Ensures the pending PCM queue contains at least `requiredFrameCount` frames.
   */
  private ensureDecodedFrames(requiredFrameCount: number): void {
    while (!this.ended && this.getPendingFrameCount() < requiredFrameCount) {
      this.decodeNextPage()
    }
  }

  /**
   * Appends decoded PCM into a growable pending queue without re-slicing on read.
   */
  private appendPendingPcm(pcm: Float32Array<ArrayBufferLike>): void {
    if (pcm.length === 0) {
      return
    }

    const activeSamples = this.getPendingSampleCount()
    this.ensurePendingPcmCapacity(pcm.length, activeSamples)

    this.pendingPcm.set(pcm, this.pendingPcmEnd)
    this.pendingPcmEnd += pcm.length
  }

  private clearPendingPcm(): void {
    this.pendingPcmStart = 0
    this.pendingPcmEnd = 0
  }

  private ensurePendingPcmCapacity(additionalSamples: number, activeSamples: number): void {
    if (activeSamples === 0) {
      if (this.pendingPcm.length < additionalSamples) {
        this.pendingPcm = new Float32Array(additionalSamples)
      }

      this.clearPendingPcm()
      return
    }

    if (this.pendingPcm.length - this.pendingPcmEnd >= additionalSamples) {
      return
    }

    if (this.pendingPcmStart > 0) {
      this.pendingPcm.copyWithin(0, this.pendingPcmStart, this.pendingPcmEnd)
      this.pendingPcmStart = 0
      this.pendingPcmEnd = activeSamples
    }

    if (this.pendingPcm.length - this.pendingPcmEnd >= additionalSamples) {
      return
    }

    const grown = new Float32Array(
      Math.max(this.pendingPcmEnd + additionalSamples, Math.max(1024, this.pendingPcm.length * 2))
    )
    grown.set(this.pendingPcm.subarray(0, this.pendingPcmEnd), 0)
    this.pendingPcm = grown
  }

  private getPendingSampleCount(): number {
    return this.pendingPcmEnd - this.pendingPcmStart
  }

  private getPendingFrameCount(): number {
    return this.getPendingSampleCount() / OUTPUT_CHANNELS
  }

  /**
   * Converts a region-local timestamp to an Ogg granule position.
   *
   * RFC 7845 stores positions in 48 kHz samples including the stream pre-skip,
   * so the decoder must add `preSkip` before asking the index for a page anchor.
   */
  private secondsToGranulePosition(seconds: number): bigint {
    const sampleOffset = BigInt(Math.round(Math.max(0, seconds) * OPUS_SAMPLE_RATE))

    // RFC 7845 Sections 4.2 and 4.3: sample positions in the container are stored
    // as pre-skip-inclusive 48 kHz granule positions.
    return sampleOffset + BigInt(this.index.preSkip)
  }

  /**
   * Normalizes decoded output to the mixer's fixed stereo format.
   *
   * Mono regions are duplicated into left/right channels so the centralized
   * mixer can treat every track buffer as stereo PCM.
   */
  private toStereoInterleaved(channelData: Float32Array[], frameCount: number): Float32Array {
    if (frameCount <= 0) {
      return new Float32Array(0)
    }

    const leftChannel = channelData[0].subarray(0, frameCount)
    const rightChannel = (channelData[1] ?? channelData[0]).subarray(0, frameCount)
    const output = new Float32Array(frameCount * OUTPUT_CHANNELS)
    interleave([leftChannel, rightChannel], output, frameCount)
    return output
  }
}
